import { useMutation } from '@tanstack/react-query'

import { Project } from 'interfaces/project'

import { useQueryClient } from '@tanstack/react-query'
import GrowingTextInput from 'components/core/text-input/growing-text-input'
import BrandsPopover from 'components/elements/brands-popover'
import CompanyTaggingPopover from 'components/elements/company-tagging/company-tagging-popover'
import { LoadingScreen } from 'components/pages/requests/empty-screens'
import { Brand } from 'interfaces/brand'
import { ProjectStatus } from 'interfaces/project'
import { Ticket } from 'interfaces/ticket'
import { CompanyTag } from 'lib/api/company-tags/company-tags'
import { request } from 'lib/api/fetch-api'
import Banner from 'lib/components/banner/banner'
import CopyTextButton from 'lib/components/buttons/copy-text-button'
import IconButton from 'lib/components/buttons/icon-button'
import { Drawer } from 'lib/components/drawer/drawer'
import { IconFlyoutMenu } from 'lib/components/dropdown-icon-menu/icon-flyout-menu'
import { useFeatureFlagsContext } from 'lib/components/feature-flags/feature-flags-provider'
import Textarea from 'lib/components/textarea/textarea'
import { toast } from 'sonner'
import { PATHS } from 'lib/constants/paths'
import { displayDate } from 'lib/util/date'
import {
  ArrowRightToLineIcon,
  CheckIcon,
  CopyIcon,
  FolderOpenIcon,
  LinkIcon,
  LoaderCircleIcon,
  PencilIcon,
  Trash2Icon,
  TrashIcon,
  XIcon,
} from 'lucide-react'
import { useAllCompanyTagsContext } from 'providers/all-company-tags-provider'
import CompanyTaggingProvider, { ChangeTagAction } from 'providers/company-tagging-provider'
import { useUserContext } from 'providers/user-provider'
import { ChangeEvent, useEffect, useState } from 'react'
import ProjectAssets from '../project-assets'
import ProjectDeliverablesTable from '../project-deliverables-table'
import ProjectStatusPopover from '../project-status-popover'
import { Divider, DrawerState, ProjectUpdateType } from './project-drawer'
import ProjectPartnerContent from './project-partner-content'
interface ProjectDetailsContentProps {
  isOpen: boolean
  setOpen: (open: boolean) => void
  project: Project
  isLoading: boolean
  setDrawerState: (state: DrawerState) => void
  onRemoveDeliverablesClick: (deliverablesToRemove: Ticket[]) => void
  onUpdate?: (project?: Project, action?: ProjectUpdateType) => void
}

/**
 * @deprecated this has been migrated to the web app and any changes should be made in both web app and here.
 */
export default function ProjectDetailsContent({
  isOpen,
  setOpen,
  project,
  isLoading,
  setDrawerState,
  onRemoveDeliverablesClick,
  onUpdate,
}: ProjectDetailsContentProps) {
  const [projectName, setProjectName] = useState<string>()
  const [selectedTags, setSelectedTags] = useState([])
  const [selectedBrand, setSelectedBrand] = useState<Brand>()
  const [status, setStatus] = useState<ProjectStatus>(project?.status)
  const { companyTags } = useAllCompanyTagsContext()
  const { user } = useUserContext()
  const [editDescription, setEditDescription] = useState(false)
  const [editStatus, setEditStatus] = useState(false)
  const [description, setDescription] = useState(project?.description)
  const [showProjectNameError, setShowProjectNameError] = useState(false)
  const queryClient = useQueryClient()
  const [showDeleteProject, setShowDeleteProject] = useState(false)
  const { isFeatureFlagEnabled } = useFeatureFlagsContext()

  const updateProjectMutation = useMutation({
    mutationFn: ({
      brand,
      description,
      name,
      status,
    }: {
      brand?: Brand
      description?: string
      name?: string
      status?: ProjectStatus
    }) => {
      return request({
        endpoint: 'updateProject',
        query: { id: project.id },
        body: {
          brand_id: brand?.id || null,
          description,
          name,
          status,
        },
      })
    },
    onSuccess: async (data: { data: Project }) => {
      toast.success('Project updated')

      queryClient.setQueryData(['project', project.id], (old: Project) => ({ ...old, ...data.data }))
      onUpdate?.(data.data, ProjectUpdateType.UPDATE)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['project', project.id] })
    },
    onError: (error) => {
      toast.error('Failed to update project')
      console.error('Failed to update project', error)
    },
  })

  const duplicateProjectMutation = useMutation({
    mutationFn: () => {
      return request({
        endpoint: 'duplicateProject',
        query: { id: project.id },
      })
    },
    onSuccess: (response: { data: { id: number } }) => {
      toast.success('Project has been duplicated')
      window.location.href = `${PATHS.REQUESTS}?tab=draft&projectId=${response.data.id}&duplicate=true`
    },
    onError: () => {
      toast.error('Failed to duplicate project')
    },
  })

  const deleteProjectMutation = useMutation({
    mutationFn: () => {
      return request({
        endpoint: 'deleteProject',
        query: { id: project.id },
      })
    },
    onSuccess: () => {
      toast.success('Project deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      onUpdate?.(project, ProjectUpdateType.DELETE)
      setOpen(false)
    },
    onError: (error) => {
      toast.error('Error deleting project')
      console.error(error)
    },
  })

  function handleUpdateTaggableState(tag: CompanyTag, action: ChangeTagAction) {
    switch (action) {
      case ChangeTagAction.Select:
        setSelectedTags([...selectedTags, tag])
        break
      case ChangeTagAction.Unselect:
      case ChangeTagAction.Delete:
        setSelectedTags(selectedTags.filter((selectedTag) => selectedTag.id !== tag.id))
        break
      case ChangeTagAction.Update:
        setSelectedTags(selectedTags.map((selectedTag) => (selectedTag.id === tag.id ? tag : selectedTag)))
        break
      default:
        break
    }
  }

  function handleDescriptionChange(event: ChangeEvent<HTMLTextAreaElement>) {
    setDescription(event.target.value)
  }

  function resetDescription() {
    setDescription(project?.description)
    setEditDescription(false)
  }

  function saveDescription() {
    updateProjectMutation.mutate({ description: description.trim() })
    setEditDescription(false)
  }

  function saveProjectName() {
    if (projectName.trim() === project?.name) {
      return
    }

    if (projectName.trim() === '') {
      toast.error('Project name required')
      setShowProjectNameError(true)
      return
    }

    updateProjectMutation.mutate({ name: projectName.trim() })
  }

  function saveStatus(newStatus: ProjectStatus) {
    setStatus(newStatus)
    updateProjectMutation.mutate({ status: newStatus })
  }

  const projectDrawerHeader = () => {
    if (isLoading) {
      return (
        <div className="tw-flex tw-flex-col tw-justify-center tw-gap-2">
          <div>Loading...</div>
        </div>
      )
    }

    if (showDeleteProject) {
      return <h3 className="tw-truncate">Delete &quot;{project.name}&quot; project</h3>
    }

    return (
      <>
        <FolderOpenIcon className="lu-xl tw-text-neutral-700" />
        <div className="tw-flex tw-flex-col tw-justify-center tw-gap-2" data-testid="project-name-input">
          <div className="tw-flex tw-items-center tw-gap-2">
            <GrowingTextInput
              value={projectName || ''}
              onChange={(e) => setProjectName(e.target.value)}
              onBlur={saveProjectName}
              name="project-name"
              className="tw-w-full"
              extraWidth={50}
              hasError={showProjectNameError}
            />
            {showProjectNameError && <div className="tw-text-red-500">Project name required</div>}
            <ProjectStatusPopover status={status} open={editStatus} setOpen={setEditStatus} setStatus={saveStatus} />
          </div>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-neutral-500">
            <div>Last updated on {displayDate(project?.updatedAt)}</div>
            {project?.projectTemplate?.partner && (
              <>
                <Divider />
                <ProjectPartnerContent partner={project?.projectTemplate?.partner} />
              </>
            )}
          </div>
        </div>
      </>
    )
  }

  const ProjectDrawerBody = () => {
    if (isLoading) {
      return <LoadingScreen />
    }

    if (showDeleteProject) {
      return (
        <div className="tw-flex tw-h-full tw-items-center tw-justify-center">
          <div className="tw-flex tw-w-full tw-flex-col tw-items-center tw-gap-2 tw-text-center">
            <div className="tw-rounded-md tw-bg-flushpink-50 tw-p-2">
              <TrashIcon className="lu-xl tw-text-flushpink-500" />
            </div>
            <h4 className="tw-m-0 tw-text-neutral-500">Delete project?</h4>
            <div className="tw-m-0 tw-max-w-prose tw-text-neutral-500">
              <p className="tw-m-0">Are you sure you want to delete the project &quot;{project.name}&quot;?</p>
              <p className="tw-m-0">
                The project will be deleted, but it&apos;s requests will remain available in the request queue.
              </p>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {window.location.search.includes('duplicate=true') && (
          <Banner
            type="info"
            heading="Duplicate project"
            subheading="It may take a few minutes for all the project information to be duplicated."
          />
        )}
        <div className="tw-flex tw-items-center">
          <BrandsPopover
            onChange={(brand) => {
              setSelectedBrand(brand)
              updateProjectMutation.mutate({ brand })
            }}
            brand={selectedBrand}
            companyId={user?.companyId}
          />
          <div className="tw-ml-4 tw-mr-10 tw-h-5 tw-w-[1px] tw-bg-neutral-600" />
          <CompanyTaggingProvider
            selectedTags={selectedTags}
            updateTaggableState={handleUpdateTaggableState}
            taggableId={project?.id}
            taggableType="Project"
            warningMessage="Adding and removing project tags does not add nor remove tags from the associated requests"
          >
            <CompanyTaggingPopover />
          </CompanyTaggingProvider>
        </div>
        <div>
          <div className="tw-flex tw-gap-2">
            <h5>Description</h5>
            {!editDescription && (
              <IconButton
                className="tw-mb-4"
                color="secondary"
                onClick={() => setEditDescription(true)}
                dataTestid="edit-description"
              >
                <PencilIcon className="lu-sm" />
              </IconButton>
            )}
          </div>
          {editDescription ? (
            <div className="tw-flex tw-items-center tw-gap-2">
              <Textarea
                className="tw-flex-1"
                placeholder="Enter a description for the project"
                value={description}
                onChange={handleDescriptionChange}
              />
              <div className="tw-flex tw-gap-2">
                <IconButton color="secondary" onClick={saveDescription}>
                  <CheckIcon className="lu-sm" />
                </IconButton>
                <IconButton color="secondary" onClick={resetDescription}>
                  <XIcon className="lu-sm" />
                </IconButton>
              </div>
            </div>
          ) : (
            <div className="tw-flex tw-items-center tw-gap-2">
              <p className="tw-flex-1 tw-overflow-hidden tw-text-ellipsis tw-break-words">{description}</p>
            </div>
          )}
        </div>
        {isFeatureFlagEnabled('projects_v2') && <ProjectAssets projectId={project?.id} />}
        <ProjectDeliverablesTable
          projectId={project?.id}
          handleAddDeliverablesClick={() => setDrawerState('add-deliverables')}
          handleRemoveDeliverablesClick={onRemoveDeliverablesClick}
          handleCreateDeliverablesClick={() => setDrawerState('create-deliverables')}
          refetchProject={() => onUpdate(project, ProjectUpdateType.UPDATE)}
        />
      </div>
    )
  }

  useEffect(() => {
    function detailsHaveChanged() {
      return projectName !== project?.name || description !== project?.description || selectedTags !== project?.tags
    }

    if (!isOpen && detailsHaveChanged()) {
      onUpdate?.()
    }
  }, [description, isOpen, onUpdate, project?.description, project?.name, project?.tags, projectName, selectedTags])

  useEffect(() => {
    if (project) {
      setProjectName(project?.name)
      setSelectedBrand(project?.brand)
      setDescription(project?.description)
      setSelectedTags(project?.tags || [])
    }
  }, [project])

  useEffect(() => {
    setSelectedTags((prevSelectedTags) => {
      return prevSelectedTags
        .filter((selectedTag) => companyTags.some((companyTag) => companyTag.id === selectedTag.id))
        .map((selectedTag) => companyTags.find((tag) => tag.id === selectedTag.id))
    })
  }, [companyTags])

  const handleDuplicateProject = () => {
    duplicateProjectMutation.mutate()
  }

  return (
    <>
      <Drawer.Header setOpen={setOpen} showCloseButton={false} className="tw-static">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-pb-2 tw-pt-4">
          <div className="tw-flex tw-w-full tw-items-center tw-gap-4">
            <IconButton size="xs" color="transparent" onClick={() => setOpen(false)} className="tw-mr-2">
              <ArrowRightToLineIcon className="tw-text-neutral-700" />
            </IconButton>
            {projectDrawerHeader()}
          </div>
          {!isLoading && !showDeleteProject && (
            <div className="tw-flex tw-gap-2">
              <CopyTextButton textToCopy={window.location.href} defaultIcon={<LinkIcon />} />
              <IconFlyoutMenu color="secondary" icon={['far', 'ellipsis-v']}>
                <IconFlyoutMenu.Button className="tw-flex tw-items-center tw-gap-2" onClick={handleDuplicateProject}>
                  <CopyIcon className="tw-h-4 tw-w-4 tw-text-neutral-500" />
                  <span className="tw-text-neutral-500">Duplicate</span>
                </IconFlyoutMenu.Button>
                <IconFlyoutMenu.Button
                  className="tw-flex tw-items-center tw-gap-2"
                  onClick={() => setShowDeleteProject(true)}
                >
                  <Trash2Icon className="tw-h-4 tw-w-4 tw-text-flushpink-500" />
                  <span className="tw-text-flushpink-500">Delete</span>
                </IconFlyoutMenu.Button>
              </IconFlyoutMenu>
            </div>
          )}
        </div>
      </Drawer.Header>
      <Drawer.Body className="tw-overflow-y-auto">
        <ProjectDrawerBody />
      </Drawer.Body>
      {showDeleteProject && (
        <Drawer.Footer
          onCancel={() => setShowDeleteProject(false)}
          onSubmit={deleteProjectMutation.mutate}
          submitText={
            deleteProjectMutation.isPending ? <LoaderCircleIcon className="tw-animate-spin" /> : 'Delete project'
          }
          submitColor="red"
        />
      )}
    </>
  )
}
